import { ConversationDetails } from "@virtualprogaming/js-public-sdk";
import { isEmpty, map } from "lodash";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useChatConversations, useChatGlobalConversation } from "~/queries";
import { NoContentFound, TextInput } from "~/ui/components";
import { Boundary } from "~/ui/components/boundary";
import { useChatAuthAccessToken } from "~/ui/context";

import { Conversation } from "./components/Conversation";

const CONVERSATIONS_LIMIT = 15;

export const ChatConversations: FC = () => {
  const [limit, setLimit] = useState(CONVERSATIONS_LIMIT);
  const accessToken = useChatAuthAccessToken();
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const intl = useIntl();
  const { data: chatConversations, isLoading } = useChatConversations(
    accessToken,
    limit,
    searchTerm
  );
  const { data: globalConversation } = useChatGlobalConversation();

  const onLoadMore = () => {
    setLimit((prevLimit: number) => prevLimit + limit);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(search);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const filteredChatConversations = useMemo(() => {
    if (!chatConversations?.data) {
      return [];
    }
    let filtered = chatConversations?.data?.filter(({ id }) => id !== 1);
    if (globalConversation && search === "") {
      filtered = [
        { ...globalConversation, tags: ["global", "announcements"] },
        ...filtered,
      ];
    }
    return filtered;
  }, [chatConversations?.data, globalConversation, search]);

  return (
    <div className="h-95 flex flex-col justify-between relative">
      <div className="h-95 scroll-auto scroll-hide">
        <div>
          <TextInput
            value={search}
            onChange={handleSearchChange}
            placeholder={intl.formatMessage({
              defaultMessage: "Search users",
              id: "JjGgXI",
            })}
            boxSize="lg"
            className="bg-dark"
            wrapperClassname="mg-sm bg-dark"
          />
          <Boundary
            centerStatus
            isLoading={isLoading}
            iconClassname="text-white"
          >
            <div>
              {isEmpty(filteredChatConversations) && !isLoading ? (
                <div className="containerContent">
                  <NoContentFound>
                    <span className="text-white">
                      <FormattedMessage
                        defaultMessage="No conversations found"
                        id="WbCasI"
                      />
                    </span>
                  </NoContentFound>
                </div>
              ) : (
                map(
                  filteredChatConversations,
                  (conversation: ConversationDetails) => (
                    <Conversation
                      key={conversation.id}
                      conversation={conversation}
                      isGlobal={conversation?.tags?.includes("global")}
                    />
                  )
                )
              )}
            </div>
          </Boundary>
        </div>
      </div>
      {!chatConversations?.data ||
        (!isEmpty(chatConversations?.data) &&
          chatConversations?.count &&
          limit < chatConversations?.count && (
            <span
              className="bg-transparent-55 pointer pd-lg pd-t-sm pd-b-sm borderR-1 absolute bottom-50px left-30p"
              onClick={onLoadMore}
            >
              <FormattedMessage defaultMessage="Load more" id="00LcfG" />
            </span>
          ))}
    </div>
  );
};
